import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import * as FaIcons from "react-icons/fa";
import { motion } from "framer-motion";
import ScrollToTop from '../scrollToTop/ScrollToTop';

const ServiceOverview = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [hasOverflow, setHasOverflow] = useState({});
  const contentRefs = useRef({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/services`
        );
        setServices(res.data);
      } catch (err) {
        console.error("Failed to fetch services:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    // Check overflow after content is rendered
    const newOverflows = {};
    services.forEach(service => {
      const element = contentRefs.current[service.id];
      if (element) {
        newOverflows[service.id] = element.scrollHeight > 128; // 128px = max-h-32
      }
    });
    setHasOverflow(newOverflows);
  }, [services, expanded]); // Re-check when services or expanded state changes

  const toggleExpand = (id) => {
    setExpanded(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  return (
    <>
      <ScrollToTop/>
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section className="bg-blue-100 text-white py-20 text-center mt-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="container mx-auto px-4"
          >
            <h1 className="text-3xl md:text-4xl font-semibold mb-4 text-blue-800">
              Our Services
            </h1>
            <p className="text-xl text-blue-950 max-w-2xl mx-auto">
              Professional financial services for our community
            </p>
          </motion.div>
        </section>

        {/* Services Grid */}
        <section className="container mx-auto px-4 py-16">
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ repeat: Infinity, duration: 1 }}
                className="text-blue-600 text-4xl"
              >
                <FaIcons.FaSpinner />
              </motion.div>
            </div>
          ) : services.length > 0 ? (
            <div className="grid grid-cols-1 gap-8">
              {services.map((service) => {
                const Icon = FaIcons[service.icon] || FaIcons.FaHandshake;
                const isExpanded = expanded[service.id];
                
                return (
                  <motion.div
                    key={service.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4 }}
                    className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow w-full"
                  >
                    <div className="p-6">
                      <div className="flex items-center mb-4">
                        <div className="bg-blue-100 p-3 rounded-full mr-4">
                          <Icon className="text-blue-600 text-xl" />
                        </div>
                        <h3 className="text-xl font-semibold text-gray-800">
                          {service.title}
                        </h3>
                      </div>
                      
                      <div 
                        ref={el => contentRefs.current[service.id] = el}
                        className={`overflow-hidden transition-all duration-300 ${isExpanded ? 'max-h-[2000px]' : 'max-h-32'}`}
                      >
                        <div 
                          className="ql-editor"
                          style={{
                            padding: 0,
                            fontSize: '1rem',
                            lineHeight: '1.5',
                            color: '#4b5563'
                          }}
                          dangerouslySetInnerHTML={{ __html: service.summary }} 
                        />
                      </div>
                      
                      {hasOverflow[service.id] && (
                        <button 
                          onClick={() => toggleExpand(service.id)}
                          className="text-blue-600 hover:text-blue-800 font-medium mt-3 flex items-center transition-colors"
                        >
                          {isExpanded ? (
                            <>
                              Read Less <FaIcons.FaChevronUp className="ml-1 text-sm" />
                            </>
                          ) : (
                            <>
                              Read More <FaIcons.FaChevronDown className="ml-1 text-sm" />
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-20 bg-white rounded-lg shadow-sm">
              <FaIcons.FaInfoCircle className="text-4xl text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-800">
                No services available
              </h3>
            </div>
          )}
        </section>
      </div>
    </>
  );
};

export default ServiceOverview;